import React, {useRef, useState} from 'react'

type Position = {top: number; left: number}

type UseVariableSuggestionsProps = {
 options: string[]
 debounceTime?: number
}

export const useVariableSuggestions = ({
 options,
 debounceTime = 250,
}: UseVariableSuggestionsProps) => {
 const textareaRef = useRef<HTMLTextAreaElement | null>(
  null
 )
 const debounceRef = useRef<ReturnType<
  typeof setTimeout
 > | null>(null)

 const [showSuggestions, setShowSuggestions] =
  useState(false)
 const [filteredOptions, setFilteredOptions] = useState<
  string[]
 >([])
 const [dropdownPos, setDropdownPos] = useState<Position>({
  top: 0,
  left: 0,
 })
 const cursorRef = useRef(0)
 const [activeIndex, setActiveIndex] = useState(0)

 const getCaretCoordinates = (
  textarea: HTMLTextAreaElement,
  position: number
 ) => {
  const div = document.createElement('div')
  const style = window.getComputedStyle(textarea)

  const properties = [
   'fontSize',
   'fontFamily',
   'fontWeight',
   'lineHeight',
   'padding',
   'border',
   'whiteSpace',
   'wordWrap',
  ]

  properties.forEach((prop) =>
   div.style.setProperty(prop, style.getPropertyValue(prop))
  )

  div.style.position = 'absolute'
  div.style.visibility = 'hidden'
  div.style.whiteSpace = 'pre-wrap'
  div.style.width = textarea.offsetWidth + 'px'

  div.textContent = textarea.value.substring(0, position)

  const span = document.createElement('span')
  span.textContent = '|'
  div.appendChild(span)

  document.body.appendChild(div)
  const {offsetTop, offsetLeft} = span
  document.body.removeChild(div)

  return {top: offsetTop, left: offsetLeft}
 }

 const runSuggestionLogic = (
  value: string,
  cursor: number
 ) => {
  const textBeforeCursor = value.slice(0, cursor)
  const lastOpen = textBeforeCursor.lastIndexOf('<')
  const lastClose = textBeforeCursor.lastIndexOf('>')

  if (lastOpen > lastClose) {
   const searchText = textBeforeCursor.slice(lastOpen + 1)

   const filtered = options.filter((opt) =>
    opt.toLowerCase().includes(searchText.toLowerCase())
   )

   setFilteredOptions(filtered)
   setActiveIndex(0)
   setShowSuggestions(true)

   if (textareaRef.current) {
    const coords = getCaretCoordinates(
     textareaRef.current,
     cursor
    )

    setDropdownPos({
     top: coords.top + 60,
     left: coords.left + 8,
    })
   }
  } else {
   setShowSuggestions(false)
  }
 }

 const applySuggestion = (
  currentText: string,
  option: string
 ) => {
  const beforeCursor = currentText.slice(
   0,
   cursorRef.current
  )
  const afterCursor = currentText.slice(cursorRef.current)

  const lastOpen = beforeCursor.lastIndexOf('<')
  const insertion = `<${option}>`

  const newText =
   beforeCursor.slice(0, lastOpen) + insertion + afterCursor

  requestAnimationFrame(() => {
   const pos = lastOpen + insertion.length
   textareaRef.current?.setSelectionRange(pos, pos)
   textareaRef.current?.focus()
   cursorRef.current = pos
  })

  setShowSuggestions(false)
  return newText
 }

 const handleKeyDown = (
  e: React.KeyboardEvent<HTMLTextAreaElement>,
  currentText: string,
  setValue: (val: string) => void
 ) => {
  if (!showSuggestions && e.key !== 'Escape') return

  switch (e.key) {
   case 'ArrowDown':
    e.preventDefault()
    setActiveIndex((prev) =>
     prev < filteredOptions.length - 1 ? prev + 1 : 0
    )
    break

   case 'ArrowUp':
    e.preventDefault()
    setActiveIndex((prev) =>
     prev > 0 ? prev - 1 : filteredOptions.length - 1
    )
    break

   case 'Enter':
   case 'Tab':
    e.preventDefault()
    if (filteredOptions[activeIndex]) {
     const newText = applySuggestion(
      currentText,
      filteredOptions[activeIndex]
     )
     setValue(newText)
    }
    break

   case 'Escape':
    e.preventDefault()
    setShowSuggestions(false)
    break
  }
 }
 const handleTextChange = (
  value: string,
  cursor: number,
  setValue: (val: string) => void
 ) => {
  setValue(value)
  cursorRef.current = cursor

  if (debounceRef.current) {
   clearTimeout(debounceRef.current)
  }

  debounceRef.current = setTimeout(() => {
   runSuggestionLogic(value, cursorRef.current)
  }, debounceTime)
 }

 return {
  textareaRef,
  debounceRef,
  showSuggestions,
  filteredOptions,
  dropdownPos,
  activeIndex,
  handleTextChange,
  applySuggestion,
  handleKeyDown,
  setActiveIndex,
  setShowSuggestions,
 }
}
